// FAQ page STRUCTURE — categories, subgroup labels, page copy and the raw
// entry → question mapping. Everything here is small, role-independent and
// shipped to every viewer.
//
// WHY IT IS SEPARATE FROM THE QUESTIONS
// ------------------------------------
// The questions are confidential per role (Marin, 2026-08-10: "für andere
// rollen darfst du nicht anzeigen, der Agent und Agency darf sicher nicht
// wissen was alles die Clubs haben!"). They are therefore loaded per BRANCH
// from constants/faq/faqBranchData.js and must NOT ride in the main chunk.
// FaqSearch/FaqContactCard need FAQ_COPY, and faqBranchData needs the mapping —
// so both live here, in a module that imports no dataset at all. A component
// importing the full dataset (faqData.js) would drag every tier's questions
// back into the bundle; faqDatasetIsolation.test.js guards that.

// The consolidated tab structure (~9 top categories from the tier files'
// section clusters; the design HTML's 6 categories were placeholders).
export const FAQ_CATEGORIES = [
  { id: "account", label: "Account & profile" },
  { id: "requests", label: "Requests" },
  { id: "pitching", label: "Pitching & applications" },
  { id: "trusted-agent", label: "Trusted Agent" },
  { id: "scouting", label: "Search, players & radar" },
  { id: "contracts", label: "Contracts & club tools" },
  { id: "chat", label: "Chat & inbox" },
  { id: "team", label: "Team & organisation" },
  { id: "notifications", label: "Notifications & emails" },
];

// Perspective labels (Marin 2026-08-12): one surface, two names. The feature
// clubs know as "Trusted Agent" is "Search Requests" on the agent side (the
// ☰-menu title, useHeaderNavigation.js:249; Marin's wording) — so a viewer
// WITHOUT a club branch gets the agent-side name on the tab. Club-side viewers
// (1c/2c, incl. dual roles) keep "Trusted Agent"; viewers with neither branch
// never see the tab (count 0). Both labels are chrome rows with all 16 locale
// columns.
export const FAQ_CATEGORY_PERSPECTIVE_LABELS = {
  "trusted-agent": "Search Requests",
  // IA wave: the contracts tab is club-titled for club-side viewers and names
  // the agent-side content (Last Minute, Lounge, scout tips) for the rest.
  contracts: "Contracts & opportunities",
};

export const categoryLabelFor = (category, branches) => {
  const alt = FAQ_CATEGORY_PERSPECTIVE_LABELS[category.id];
  if (!alt) return category.label;
  const owned = branches instanceof Set ? branches : new Set(branches || []);
  if (owned.has("1c") || owned.has("2c")) return category.label;
  return alt;
};

// Subgroup labels (shown as headers inside a tab from FAQ_SUBGROUP_THRESHOLD
// visible questions upward). Keys are stable; labels are i18n source strings.
export const FAQ_GROUP_LABELS = {
  "acc-session": "Account & session",
  "acc-kyc": "Identity verification (KYC)",
  "acc-profile": "Profile & settings",
  "acc-roles": "Roles & visibility",
  "acc-staff-profile": "Your profile for clubs",
  "acc-fifa": "FIFA license",
  "req-create": "Create a request (club)",
  "req-manage": "Manage club requests",
  // Agent-IA wave (Marin 2026-08-13): from the agent's perspective the two
  // marketplaces are their own topics on the requests tab, named exactly like
  // the app names them (Inbox tabs / mega menu). The old pitching-tab group
  // "Browse club requests & pitch" moved here entirely (it was 1a-only);
  // "Your requests as an agent" became "Agent Requests" and also carries the
  // agency-to-agency response questions. Order in the tab: Club Requests →
  // Agent Requests → Request verification. Club-side viewers see none of
  // these two groups (1a rows only) — their tab is unchanged.
  "req-club-requests": "Club Requests",
  "req-agent-own": "Agent Requests",
  // FAQ-IA wave (Marin 2026-08-13, F6): the staff job board lives under
  // "Requests" in the app nav, its tabs are "All Listed Requests" and
  // "Applied" (StaffTendersHeader.js) — the old pitching-tab groups
  // "Find job openings"/"Apply yourself" moved here under exactly those
  // names. Order in the tab: club groups first (req-create/req-manage),
  // then the staff board groups, then Request verification. "Join a club"
  // (invitation flow) has no competing app name and stays on pitching.
  "req-staff-listed": "All Listed Requests",
  "req-staff-applied": "Applied",
  "req-verification": "Request verification",
  // FAQ-IA wave (F3): the club page's H1 is "View Pitches" — the section
  // carries the app's own name instead of the generic "Review agent proposals".
  "pitch-review": "View Pitches",
  "pitch-join": "Join a club",
  "ta-agent": "Search on a club's behalf",
  "ta-club": "Commission a search (club)",
  "scout-search": "Search & public profiles",
  "scout-tools": "Tools for everyone",
  // FAQ-IA wave (F4/F5): named exactly like the app surfaces — top-nav
  // "My Players" (navigation.js) and "Radar" (☰ menu / page H1s, both sides).
  "scout-players": "My Players",
  "scout-radar": "Radar",
  "con-signing": "Contracts & signing",
  // IA wave (Marin 2026-08-12): the catch-all groups "Last-Minute, lounge &
  // scouting" and "More club tools" buried the individual features — each
  // feature now carries its own section header. "con-scout-requests" is shared
  // by the agent question and the club questions of the same tab; a viewer
  // only ever sees their own side's rows under it.
  "con-last-minute": "Last Minute",
  // FAQ-IA wave (F8): the app writes "Scout Requests" (☰ menu, quick pills,
  // Inbox tab) — casing follows the app.
  "con-scout-requests": "Scout Requests",
  "con-lounge": "Agent Lounge",
  "con-friendlies": "Friendlies",
  "con-availability-market": "Player availability & Live Market",
  // FAQ-IA wave (F7): the surface is the "My Staff" tab on My Club
  // (MyClub.js:107) — not a generic "Staff".
  "con-staff": "My Staff",
  "chat-mechanics": "Chat mechanics",
  "chat-roles": "Agent & club chat",
  // FAQ-IA wave (F9): the lawyer's work surface is the "Law Requests" section
  // of the Inbox (LawRequestsSection.js) — the question moved from the team
  // tab (which the lawyer's app has no counterpart for) to the chat tab.
  "chat-law-requests": "Law Requests",
  // FAQ-IA wave (F1/F2): the app calls these surfaces "Taskboard"
  // (navigation.js:62, AgencyTaskboard.js) and "My Agency"
  // (navigation.js:58, AgencyProfile.js).
  "team-agency-shared": "Taskboard",
  "team-agency-member": "Agency membership",
  "team-agency-admin": "My Agency",
  "team-partners": "Partner agencies",
  "team-agency-pitches": "Agency pitches",
  "team-club-roles": "Club roles & permissions",
  "team-club-staff": "Club team management",
  "notif-general": "For everyone",
  "notif-agent": "For agents",
  "notif-club": "For club members",
  "notif-applicants": "For applicants",
  "notif-agency-admin": "For agency admins",
  "notif-lawyer": "For lawyers",
};

// Raw tier entry → page question shape (the Ticket 1 contract FAQ.js consumes).
export const toQuestion = (e) => {
  const hasMedia = e.media === "steps";
  const priority = Boolean(e.star);
  return {
    id: e.id,
    category: e.tab,
    group: { key: `faq.group.${e.group}`, label: FAQ_GROUP_LABELS[e.group] },
    groupOrder: e.group,
    aud: e.aud,
    tier: e.aud[0],
    q: e.q,
    answer: e.a,
    steps: (e.steps || []).map((t, i) => ({ n: i + 1, text: t, key: `${e.id}.step${i + 1}` })),
    media:
      hasMedia || priority
        ? {
            priority,
            desktop: hasMedia
              ? {
                  src: null, // Ticket 3 delivers auth-protected files per the name contract
                  alt: `Annotated screenshot: ${e.q}`,
                  markers: null, // Ticket 3 delivers marker JSONs with the screenshots
                }
              : null,
            mobile:
              hasMedia && e.sets === "both"
                ? {
                    src: null,
                    alt: `Annotated mobile screenshot: ${e.q}`,
                    markers: null,
                  }
                : null,
          }
        : null,
  };
};

// Tab order → group order (per FAQ_GROUP_LABELS listing) → source order.
// Subgroup headers rely on groups being contiguous within a tab, so this sort
// runs over whatever set of modules a viewer actually loaded.
const TAB_ORDER = FAQ_CATEGORIES.reduce((m, c, i) => ({ ...m, [c.id]: i }), {});
const GROUP_ORDER = Object.keys(FAQ_GROUP_LABELS).reduce((m, g, i) => ({ ...m, [g]: i }), {});

export const sortQuestions = (questions) =>
  [...questions].sort(
    (a, b) =>
      TAB_ORDER[a.category] - TAB_ORDER[b.category] ||
      GROUP_ORDER[a.groupOrder] - GROUP_ORDER[b.groupOrder] ||
      0
  );

// Page chrome copy — i18n-ready EN source (faq-chrome.json carries the 16
// locale columns and stays statically registered).
export const FAQ_COPY = {
  kicker: "Help centre",
  title: "Frequently asked questions",
  // Role-neutral since the perspective wave (Marin 2026-08-12): agents know
  // the Trusted-Agent feature as "Search requests from clubs", so the intro
  // names the activity, not one side's feature name.
  intro:
    "How TransferPitch works, explained for your role — from requests, pitching and commissioned searches to chat, contracts and notifications.",
  searchPlaceholder: "Search the FAQ…",
  contactTitle: "Question not answered?",
  contactText: "Send our support team a message — they reply to your email.",
  contactCta: "Contact support",
};
